import React, { useContext, useEffect, useState } from "react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Add as AddIcon, Remove as RemoveIcon } from "@mui/icons-material";

import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";

import TradesTable from "../table/TradesTable";
import PositionsTable from "../table/PositionsTable";
import SpacRisk from "../SpacRisk";
import { PositionsDataContext } from "../../context/PositionsDataContext";
import {
  getPnlTableColumnsEthan1,
  getSpacPnlTableColumns,
} from "../table/columnsConfig/pnlTable";
import { CardWrapper, Section, TabNavigation } from "../ui";
import {
  getCommonBodyCellProps,
  getCommonHeadCellProps,
} from "../table/columnsConfig/commonUtils";

const SPACS_PNL = () => {
  // ===== UTILITY FUNCTIONS =====
  const checkAndClearStorageIfNewDay = () => {
    const lastVisitDate = localStorage.getItem("spacsLastVisitDate");
    const currentDate = new Date().toDateString();

    if (lastVisitDate !== currentDate) {
      // Clear all localStorage items
      localStorage.clear();

      // Set the new visit date
      localStorage.setItem("spacsLastVisitDate", currentDate);
    }
  };

  // ===== STATE =====
  const theme = useTheme();
  const { datasets } = useContext(PositionsDataContext);
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));

  // Data states
  const [allData, setAllData] = useState([]);
  const [maPositions, setMaPositions] = useState([]);
  const [tradedData, setTradedData] = useState([]);

  // Tab state
  const [spacs_activeTab, setSpacsActiveTab] = useState("tab1");

  // Filter and sorting states
  const [positionsFilter, setPositionsFilter] = useState(() => {
    checkAndClearStorageIfNewDay();
    return localStorage.getItem("spacsPnLFilter") || "";
  });

  const [spacsTradesGlobalFilter, setSpacsTradesGlobalFilter] = useState(
    () => localStorage.getItem("spacsTradesTableGlobalFilter") || ""
  );

  const [spacsTradesSorting, setSpacsTradesSorting] = useState(() => {
    const savedSorting = localStorage.getItem("spacsTradesTableSorting");
    return savedSorting
      ? JSON.parse(savedSorting)
      : [{ id: "StrategyName", desc: false }];
  });

  const [positionsSorting, setPositionsSorting] = useState(() => {
    const savedSorting = localStorage.getItem("spacsPositionsTableSorting");
    return savedSorting
      ? JSON.parse(savedSorting)
      : [{ id: "StrategyName", desc: false }];
  });

  const [positionsGlobalFilter, setPositionsGlobalFilter] = useState(
    () => localStorage.getItem("spacsPositionsTableGlobalFilter") || ""
  );

  // Scroll position states
  const [spacsTradesScrollPosition, setSpacsTradesScrollPosition] = useState(
    () => {
      const saved = localStorage.getItem("spacsTradesTableScrollPosition");
      return saved ? parseInt(saved, 10) : 0;
    }
  );

  const [spacsPnLScrollPosition, setSpacsPnLScrollPosition] = useState(() => {
    const saved = localStorage.getItem("spacsPnLTableScrollPosition");
    return saved ? parseInt(saved, 10) : 0;
  });

  const [positionsScrollPosition, setPositionsScrollPosition] = useState(() => {
    return (
      parseInt(localStorage.getItem("spacsPositionsTableScrollPosition"), 10) ||
      0
    );
  });

  // Table states
  const [expandedPnLRows, setExpandedPnLRows] = useState(() => {
    const saved = localStorage.getItem("spacsPnLTableExpandedRows");
    return saved ? JSON.parse(saved) : {};
  });

  const [expandedStrategyPnLRows, setExpandedStrategyPnLRows] = useState({});
  const [expandedSecurityPnLRows, setExpandedSecurityPnLRows] = useState({});

  // ===== CONFIG =====
  const tabs = [
    { value: "tab1", label: "SPACs P&L" },
    { value: "tab2", label: "SPACs Trades Today" },
    { value: "tab3", label: "SPACs Positions" },
    { value: "tab4", label: "SPACs P&L Rob" },
    { value: "tab5", label: "SPACs Risk" },
  ];

  // ===== EFFECTS =====
  useEffect(() => {
    if (datasets?.positions) setAllData(datasets.positions);
  }, [datasets]);

  useEffect(() => {
    if (allData?.length > 0) {
      // Filter for SPAC positions instead of Merger Arbitrage
      const maPositions = allData?.filter(
        (position) => position.StrategyType === "SPAC"
      );
      setMaPositions(maPositions);

      // Filter for positions with trades
      const positionsWithTrades = allData?.filter(
        (position) =>
          (position.buy_trade_quantity ||
            position.sell_trade_quantity ||
            position.short_trade_quantity ||
            position.cover_trade_quantity) &&
          position.StrategyType === "SPAC"
      );
      setTradedData(positionsWithTrades);
    }
  }, [allData]);

  // Persist filter states
  useEffect(() => {
    localStorage.setItem("positionsFilter", positionsFilter);
  }, [positionsFilter]);

  useEffect(() => {
    localStorage.setItem(
      "spacsTradesTableGlobalFilter",
      spacsTradesGlobalFilter || ""
    );
  }, [spacsTradesGlobalFilter]);

  useEffect(() => {
    localStorage.setItem(
      "spacsPositionsTableGlobalFilter",
      positionsGlobalFilter || ""
    );
  }, [positionsGlobalFilter]);

  // Persist sorting states
  useEffect(() => {
    localStorage.setItem(
      "spacsTradesTableSorting",
      JSON.stringify(spacsTradesSorting)
    );
  }, [spacsTradesSorting]);

  useEffect(() => {
    localStorage.setItem(
      "spacsPositionsTableSorting",
      JSON.stringify(positionsSorting)
    );
  }, [positionsSorting]);

  // Persist scroll position states
  useEffect(() => {
    localStorage.setItem(
      "spacsTradesTableScrollPosition",
      spacsTradesScrollPosition
    );
  }, [spacsTradesScrollPosition]);

  useEffect(() => {
    localStorage.setItem("spacsPnLTableScrollPosition", spacsPnLScrollPosition);
  }, [spacsPnLScrollPosition]);

  useEffect(() => {
    localStorage.setItem(
      "spacsPositionsTableScrollPosition",
      positionsScrollPosition
    );
  }, [positionsScrollPosition]);

  // Restore scroll position on tab switch and refresh
  useEffect(() => {
    if (spacs_activeTab === "tab2") {
      setTimeout(() => {
        const container = document.querySelector(
          '[class*="MuiTableContainer-root"]'
        );
        if (container) {
          const savedScroll =
            parseInt(
              localStorage.getItem("spacsTradesTableScrollPosition"),
              10
            ) || 0;
          container.scrollTop = savedScroll;
        }
      }, 200);
    }
  }, [spacs_activeTab]);

  // Save scroll position on scroll
  useEffect(() => {
    if (spacs_activeTab === "tab2") {
      const container = document.querySelector(
        '[class*="MuiTableContainer-root"]'
      );
      if (container) {
        const handleScroll = () => {
          setSpacsTradesScrollPosition(container.scrollTop);
        };
        container.addEventListener("scroll", handleScroll);
        return () => container.removeEventListener("scroll", handleScroll);
      }
    }
  }, [spacs_activeTab]);

  // Restore and save scroll position for PNL table (tab1)
  useEffect(() => {
    if (spacs_activeTab === "tab1") {
      setTimeout(() => {
        const container = document.querySelector(
          '[class*="MuiTableContainer-root"]'
        );
        if (container) {
          // Restore scroll position
          const savedScroll =
            parseInt(localStorage.getItem("spacsPnLTableScrollPosition"), 10) ||
            0;
          container.scrollTop = savedScroll;

          // Save scroll position on scroll
          const handleScroll = () => {
            setSpacsPnLScrollPosition(container.scrollTop);
          };
          container.addEventListener("scroll", handleScroll);
          return () => container.removeEventListener("scroll", handleScroll);
        }
      }, 200);
    }
  }, [spacs_activeTab]);

  // Save and restore scroll position for Positions tab
  useEffect(() => {
    if (spacs_activeTab === "tab3") {
      setTimeout(() => {
        const container = document.querySelector(
          '[class*="MuiTableContainer-root"]'
        );
        if (container) {
          // Restore scroll position
          const savedScroll =
            parseInt(
              localStorage.getItem("spacsPositionsTableScrollPosition"),
              10
            ) || 0;
          container.scrollTop = savedScroll;
          // Save scroll position on scroll
          const handleScroll = () => {
            setPositionsScrollPosition(container.scrollTop);
            localStorage.setItem(
              "spacsPositionsTableScrollPosition",
              container.scrollTop
            );
          };
          container.addEventListener("scroll", handleScroll);
          return () => container.removeEventListener("scroll", handleScroll);
        }
      }, 200);
    }
  }, [spacs_activeTab]);

  // ===== MEMOIZED VALUES =====
  const pnl_table = useMaterialReactTable({
    columns: getSpacPnlTableColumns(theme),
    data: maPositions,
    enableColumnDragging: false,
    enableGrouping: true,
    enableSorting: true,
    enablePagination: false,
    enableStickyHeader: true,
    enableStickyFooter: true, // Ensures footer sticks to the bottom
    enableColumnResizing: true,
    enableBottomToolbar: false,
    enablePinning: true,
    columnResizeMode: "onChange",
    groupedColumnMode: "remove",
    enableTopToolbar: false,
    enableColumnActions: false,
    initialState: {
      grouping: ["StrategyName"],
      sorting: [{ id: "total_pnl", desc: true }],
      density: "compact",
      expanded: expandedPnLRows,
      columnPinning: isDesktop
        ? { left: ["StrategyName"] } // Pin StrategyName on desktop
        : {}, // No columns pinned in mobile mode
    },
    state: {
      expanded: expandedPnLRows,
    },
    onExpandedChange: (updater) => {
      const newExpanded =
        typeof updater === "function" ? updater(expandedPnLRows) : updater;
      setExpandedPnLRows(newExpanded);
      localStorage.setItem(
        "mabPnLTableExpandedRows",
        JSON.stringify(newExpanded)
      );
    },
    displayColumnDefOptions: {
      "mrt-row-expand": {
        Cell: ({ row }) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              paddingLeft: `${row.depth * 2}rem`,
              cursor: "pointer",
            }}
            onClick={row.getToggleExpandedHandler()}
          >
            {row.getIsExpanded() ? (
              <RemoveIcon sx={{ fontSize: { xs: "14px", sm: "16px" } }} /> // Responsive icon size
            ) : (
              <AddIcon sx={{ fontSize: { xs: "14px", sm: "16px" } }} />
            )}
            {row.getIsGrouped() && (
              <span
                style={{
                  marginLeft: "8px",
                  fontWeight: "bold",
                  textAlign: "left",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" }, // Responsive font size
                }}
              >
                {row.groupingValue}
              </span>
            )}
          </div>
        ),
      },
    },
    muiTableHeadCellProps: {
      ...getCommonHeadCellProps(theme),
    },
    muiTableBodyCellProps: {
      ...getCommonBodyCellProps(theme),
    },
    muiTableBodyRowProps: {
      sx: {
        // Default background for even rows
        backgroundColor: theme.palette.background.paper,
        padding: "0px",
        height: {
          xs: "30px", // Taller rows for mobile readability
          sm: "25px",
        },
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: theme.palette.grey[400], // Hover effect for all rows
        },
        "&:nth-of-type(odd)": {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
    muiTableContainerProps: {
      sx: {
        height: {
          xs: "calc(100vh - 200px)", // Adjusted for mobile
          sm: "calc(100vh - 130px)", // Default for desktop
        },
        marginBottom: "0px",
        width: "100%", // Full width for responsiveness
        maxWidth: "100%", // Prevent overflow
        overflowX: "auto", // Allow horizontal scroll
        overflowY: "auto", // Ensure footer visibility
        position: "relative",
        borderRadius: 0,
        boxShadow: theme.shadows[1],
        "&::-webkit-scrollbar": {
          height: "8px",
          width: "8px", // Added for vertical scrollbar
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: theme.palette.grey[100],
          borderRadius: 0,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.grey[300],
          borderRadius: 0,
          "&:hover": {
            backgroundColor: theme.palette.grey[400],
          },
        },
      },
    },
    muiTableProps: {
      sx: {
        width: "100%", // Use full width instead of minWidth
        tableLayout: "auto", // Allow dynamic column sizing
        "& .MuiTableHead-root": {
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
        "& .MuiTableHead-root .MuiTableRow-root": {
          height: "auto",
          minHeight: {
            xs: "30px", // Taller header for mobile
            sm: "35px",
          },
        },
        "& .MuiTableHead-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(0.5, 0.25, 0.25, 0.25),
            sm: theme.spacing(0.75, 0.5, 0.25, 0.5),
          },
          "& .Mui-TableHeadCell-Content": {
            width: "100%",
            maxWidth: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
            display: "flex",
            alignItems: "center",
          },
          "& .Mui-TableHeadCell-Content-Labels": {
            width: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
          },
          "& .Mui-TableHeadCell-Content-Wrapper": {
            whiteSpace: "normal !important",
            textOverflow: "unset",
            overflow: "visible",
          },
        },
        "& .MuiTableFooter-root": {
          borderTop: `2px solid ${theme.palette.divider}`,
          position: "sticky",
          bottom: 0,
          zIndex: 100, // Increased for visibility
          backgroundColor: theme.palette.primary.dark,
        },
        "& .MuiTableFooter-root .MuiTableRow-root": {
          height: "40px",
        },
        "& .MuiTableFooter-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(1, 0.5), // Reduced for mobile
            sm: theme.spacing(1.5, 1),
          },
          lineHeight: "1.2",
          backgroundColor: theme.palette.primary.dark,
          fontSize: {
            xs: "0.75rem",
            sm: "0.875rem",
          },
          color: theme.palette.primary.contrastText,
        },
      },
    },
  });
  const pnl_table_Rob1 = useMaterialReactTable({
    columns: getPnlTableColumnsEthan1(theme),
    data: maPositions,
    enableColumnDragging: false,
    enableGrouping: true,
    enableSorting: true,
    enablePagination: false,
    enableStickyHeader: true,
    enableStickyFooter: true, // Ensures footer sticks to the bottom
    enableColumnResizing: true,
    enableBottomToolbar: false,
    enablePinning: true,
    columnResizeMode: "onChange",
    groupedColumnMode: "remove",
    enableTopToolbar: false,
    enableColumnActions: false,
    initialState: {
      grouping: ["StrategyName"],
      sorting: [{ id: "total_pnl", desc: true }],
      density: "compact",
      expanded: expandedStrategyPnLRows,
      columnPinning: isDesktop
        ? { left: ["StrategyName"] } // Pin StrategyName on desktop
        : {}, // No columns pinned in mobile mode
    },
    state: {
      expanded: expandedStrategyPnLRows,
    },
    onExpandedChange: (updater) => {
      const newExpanded =
        typeof updater === "function"
          ? updater(expandedStrategyPnLRows)
          : updater;
      setExpandedStrategyPnLRows(newExpanded);
      localStorage.setItem(
        "mabStrategyPnLTableExpandedRows",
        JSON.stringify(newExpanded)
      );
    },
    displayColumnDefOptions: {
      "mrt-row-expand": {
        Cell: ({ row }) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              paddingLeft: theme.spacing(row.depth * 2),
              gap: 1,
              cursor: "pointer",
            }}
            onClick={row.getToggleExpandedHandler()}
          >
            {row.getIsExpanded() ? (
              <RemoveIcon sx={{ fontSize: { xs: 14, sm: 16 } }} /> // Responsive icon size
            ) : (
              <AddIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
            )}
            {row.getIsGrouped() && (
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" }, // Responsive font size
                }}
              >
                {row.groupingValue}
              </Typography>
            )}
          </Box>
        ),
      },
    },
    muiTableHeadCellProps: {
      ...getCommonHeadCellProps(theme),
    },
    muiTableBodyCellProps: {
      ...getCommonBodyCellProps(theme),
    },
    muiTableBodyRowProps: {
      sx: {
        // Default background for even rows
        backgroundColor: theme.palette.background.paper,
        padding: "0px",
        height: {
          xs: "30px", // Taller rows for mobile readability
          sm: "25px",
        },
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: theme.palette.grey[400], // Hover effect for all rows
        },
        "&:nth-of-type(odd)": {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
    muiTableContainerProps: {
      sx: {
        height: {
          xs: "calc(100vh - 200px)", // Adjusted for mobile
          sm: "calc(100vh - 130px)", // Default for desktop
        },
        marginBottom: "0px",
        width: "100%", // Full width for responsiveness
        maxWidth: "100%", // Prevent overflow
        overflowX: "auto", // Allow horizontal scroll
        overflowY: "auto", // Ensure footer visibility
        position: "relative",
        borderRadius: 0,
        boxShadow: theme.shadows[1],
        "&::-webkit-scrollbar": {
          height: "8px",
          width: "8px", // Added for vertical scrollbar
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: theme.palette.grey[100],
          borderRadius: 0,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.grey[300],
          borderRadius: 0,
          "&:hover": {
            backgroundColor: theme.palette.grey[400],
          },
        },
      },
    },
    muiTableProps: {
      sx: {
        width: "100%", // Use full width instead of minWidth
        tableLayout: "auto", // Allow dynamic column sizing
        "& .MuiTableHead-root": {
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
        "& .MuiTableHead-root .MuiTableRow-root": {
          height: "auto",
          minHeight: {
            xs: "30px", // Taller header for mobile
            sm: "35px",
          },
        },
        "& .MuiTableHead-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(0.5, 0.25, 0.25, 0.25),
            sm: theme.spacing(0.75, 0.5, 0.25, 0.5),
          },
          "& .Mui-TableHeadCell-Content": {
            width: "100%",
            maxWidth: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
            display: "flex",
            alignItems: "center",
          },
          "& .Mui-TableHeadCell-Content-Labels": {
            width: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
          },
          "& .Mui-TableHeadCell-Content-Wrapper": {
            whiteSpace: "normal !important",
            textOverflow: "unset",
            overflow: "visible",
          },
        },
        "& .MuiTableFooter-root": {
          borderTop: `2px solid ${theme.palette.divider}`,
          position: "sticky",
          bottom: 0,
          zIndex: 100, // Increased for visibility
          backgroundColor: theme.palette.primary.dark,
        },
        "& .MuiTableFooter-root .MuiTableRow-root": {
          height: "40px",
        },
        "& .MuiTableFooter-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(1, 0.5), // Reduced for mobile
            sm: theme.spacing(1.5, 1),
          },
          lineHeight: "1.2",
          backgroundColor: theme.palette.primary.dark,
          fontSize: {
            xs: "0.75rem",
            sm: "0.875rem",
          },
          color: theme.palette.primary.contrastText,
        },
      },
    },
  });
  const pnl_table_Rob2 = useMaterialReactTable({
    columns: getPnlTableColumnsEthan1(theme),
    data: maPositions,
    enableColumnDragging: false,
    enableGrouping: true,
    enableSorting: true,
    enablePagination: false,
    enableStickyHeader: true,
    enableStickyFooter: true, // Ensures footer sticks to the bottom
    enableColumnResizing: true,
    enableBottomToolbar: false,
    enablePinning: true,
    columnResizeMode: "onChange",
    groupedColumnMode: "remove",
    enableTopToolbar: false,
    enableColumnActions: false,
    initialState: {
      grouping: ["StrategyName"],
      sorting: [{ id: "total_pnl", desc: false }],
      density: "compact",
      expanded: expandedSecurityPnLRows,
      // columnPinning: isDesktop
      //   ? { left: ["StrategyName"] } // Pin StrategyName on desktop
      //   : {}, // No columns pinned in mobile mode
    },
    state: {
      expanded: expandedSecurityPnLRows,
    },
    onExpandedChange: (updater) => {
      const newExpanded =
        typeof updater === "function"
          ? updater(expandedSecurityPnLRows)
          : updater;
      setExpandedSecurityPnLRows(newExpanded);
      localStorage.setItem(
        "mabSecurityPnLTableExpandedRows",
        JSON.stringify(newExpanded)
      );
    },
    displayColumnDefOptions: {
      "mrt-row-expand": {
        Cell: ({ row }) => (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              paddingLeft: theme.spacing(row.depth * 2),
              gap: 1,
              cursor: "pointer",
            }}
            onClick={row.getToggleExpandedHandler()}
          >
            {row.getIsExpanded() ? (
              <RemoveIcon sx={{ fontSize: { xs: 14, sm: 16 } }} /> // Responsive icon size
            ) : (
              <AddIcon sx={{ fontSize: { xs: 14, sm: 16 } }} />
            )}
            {row.getIsGrouped() && (
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "bold",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  fontSize: { xs: "0.75rem", sm: "0.875rem" }, // Responsive font size
                }}
              >
                {row.groupingValue}
              </Typography>
            )}
          </Box>
        ),
      },
    },
    muiTableHeadCellProps: {
      ...getCommonHeadCellProps(theme),
    },
    muiTableBodyCellProps: {
      ...getCommonBodyCellProps(theme),
    },
    muiTableBodyRowProps: {
      sx: {
        // Default background for even rows
        backgroundColor: theme.palette.background.paper,
        padding: "0px",
        height: {
          xs: "30px", // Taller rows for mobile readability
          sm: "25px",
        },
        transition: "background-color 0.2s ease",
        "&:hover": {
          backgroundColor: theme.palette.grey[400], // Hover effect for all rows
        },
        "&:nth-of-type(odd)": {
          backgroundColor: theme.palette.action.hover,
        },
      },
    },
    muiTableContainerProps: {
      sx: {
        height: {
          xs: "calc(100vh - 200px)", // Adjusted for mobile
          sm: "calc(100vh - 130px)", // Default for desktop
        },
        marginBottom: "0px",
        width: "100%", // Full width for responsiveness
        maxWidth: "100%", // Prevent overflow
        overflowX: "auto", // Allow horizontal scroll
        overflowY: "auto", // Ensure footer visibility
        position: "relative",
        borderRadius: 0,
        boxShadow: theme.shadows[1],
        "&::-webkit-scrollbar": {
          height: "8px",
          width: "8px", // Added for vertical scrollbar
        },
        "&::-webkit-scrollbar-track": {
          backgroundColor: theme.palette.grey[100],
          borderRadius: 0,
        },
        "&::-webkit-scrollbar-thumb": {
          backgroundColor: theme.palette.grey[300],
          borderRadius: 0,
          "&:hover": {
            backgroundColor: theme.palette.grey[400],
          },
        },
      },
    },
    muiTableProps: {
      sx: {
        width: "100%", // Use full width instead of minWidth
        tableLayout: "auto", // Allow dynamic column sizing
        "& .MuiTableHead-root": {
          borderBottom: `2px solid ${theme.palette.divider}`,
        },
        "& .MuiTableHead-root .MuiTableRow-root": {
          height: "auto",
          minHeight: {
            xs: "30px", // Taller header for mobile
            sm: "35px",
          },
        },
        "& .MuiTableHead-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(0.5, 0.25, 0.25, 0.25),
            sm: theme.spacing(0.75, 0.5, 0.25, 0.5),
          },
          "& .Mui-TableHeadCell-Content": {
            width: "100%",
            maxWidth: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
            display: "flex",
            alignItems: "center",
          },
          "& .Mui-TableHeadCell-Content-Labels": {
            width: "100%",
            whiteSpace: "normal !important",
            textOverflow: "unset",
          },
          "& .Mui-TableHeadCell-Content-Wrapper": {
            whiteSpace: "normal !important",
            textOverflow: "unset",
            overflow: "visible",
          },
        },
        "& .MuiTableFooter-root": {
          borderTop: `2px solid ${theme.palette.divider}`,
          position: "sticky",
          bottom: 0,
          zIndex: 100, // Increased for visibility
          backgroundColor: theme.palette.primary.dark,
        },
        "& .MuiTableFooter-root .MuiTableRow-root": {
          height: "40px",
        },
        "& .MuiTableFooter-root .MuiTableCell-root": {
          padding: {
            xs: theme.spacing(1, 0.5), // Reduced for mobile
            sm: theme.spacing(1.5, 1),
          },
          lineHeight: "1.2",
          backgroundColor: theme.palette.primary.dark,
          fontSize: {
            xs: "0.75rem",
            sm: "0.875rem",
          },
          color: theme.palette.primary.contrastText,
        },
      },
    },
  });

  // ===== HANDLERS =====
  const handleTabClick = (tab) => {
    setSpacsActiveTab(tab);
    localStorage.setItem("spacs_activeTab", tab);
  };

  // ===== RENDER LOGIC =====
  // (No conditional rendering logic in this component)

  // ===== RETURN =====
  return (
    <Box sx={{ width: "100%" }}>
      {/* Tab Navigation */}
      <CardWrapper sx={{ mb: 3 }}>
        <TabNavigation
          tabs={tabs}
          activeTab={spacs_activeTab}
          onTabChange={handleTabClick}
        />
      </CardWrapper>

      {/* Tab content */}
      <Box sx={{ backgroundColor: theme.palette.background.default }}>
        {spacs_activeTab === "tab1" && (
          <Box
            sx={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              backgroundColor: theme.palette.background.paper,
              borderRadius: 0,
              boxShadow: theme.shadows[1],
            }}
          >
            <MaterialReactTable table={pnl_table} />
          </Box>
        )}
        {spacs_activeTab === "tab2" && (
          <TradesTable
            data={tradedData}
            title="SPAC Trades Today"
            globalFilter={spacsTradesGlobalFilter}
            setGlobalFilter={setSpacsTradesGlobalFilter}
            sorting={spacsTradesSorting}
            setSorting={setSpacsTradesSorting}
            scrollPosition={spacsTradesScrollPosition}
          />
        )}
        {spacs_activeTab === "tab3" && (
          <PositionsTable
            data={maPositions}
            title="SPAC Positions"
            globalFilter={positionsGlobalFilter}
            setGlobalFilter={setPositionsGlobalFilter}
            sorting={positionsSorting}
            setSorting={setPositionsSorting}
            scrollPosition={positionsScrollPosition}
          />
        )}
        {spacs_activeTab === "tab4" && (
          <Section>
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                backgroundColor: theme.palette.background.paper,
                borderRadius: 0,
                boxShadow: theme.shadows[1],
              }}
            >
              <Box
                sx={{
                  // width: "fit-content",
                  display: "flex",
                  flexDirection: "row",
                  overflowY: "hidden",
                  gap: 0,
                }}
              >
                <Box
                  sx={{
                    "& .MuiTableContainer-root": { m: 0, p: 0 },
                    width: { xs: "50%", md: "17%" },
                  }}
                >
                  <MaterialReactTable table={pnl_table_Rob1} />
                </Box>
                <Box
                  sx={{
                    "& .MuiTableContainer-root": { m: 0, p: 0 },
                    width: { xs: "50%", md: "17%" },
                  }}
                >
                  <MaterialReactTable table={pnl_table_Rob2} />
                </Box>
              </Box>
            </Box>
          </Section>
        )}
        {spacs_activeTab === "tab5" && <SpacRisk />}
      </Box>
    </Box>
  );
};

export default SPACS_PNL;
